// Hook owning all DataTable query + selection state.
// - Reducer drives the wire-shape ListQuery; setters dispatch typed actions.
// - Search input is buffered (pendingSearch) and debounced into query.search.
// - Selection lives separately because it does not affect the server query.

import { useEffect, useMemo, useReducer, useState } from "react";
import type { Dispatch } from "react";
import type {
  DataTableSelection,
  DataTableState,
  FilterPrimitive,
  ListQuery,
  SortOrder,
} from "./types.js";
import { DEFAULT_SEARCH_DEBOUNCE_MS } from "./types.js";

export interface UseDataTableOptions {
  pageSize?: number;
  defaultSort?: { sortBy: string; order: "asc" | "desc" };
  searchDebounceMs?: number;
}

type Action =
  | { type: "page"; page: number }
  | { type: "limit"; limit: number }
  | { type: "sort"; sortBy: string }
  | { type: "search"; search: string }
  | { type: "dateRange"; from?: string; to?: string }
  | { type: "filter"; key: string; value: FilterPrimitive | undefined }
  | { type: "clearFilters" };

const RESERVED_KEYS: ReadonlySet<string> = new Set([
  "page", "limit", "sortBy", "order", "dateFrom", "dateTo", "search",
]);

function nextOrder(current: SortOrder | undefined): SortOrder {
  if (current == null) return "asc";
  if (current === "asc") return "desc";
  return null;
}

function applySort(state: ListQuery, sortBy: string): ListQuery {
  const same = state.sortBy === sortBy;
  const order = same ? nextOrder(state.order) : "asc";
  if (order === null) {
    const { sortBy: _s, order: _o, ...rest } = state;
    return { ...rest, page: 1 };
  }
  return { ...state, sortBy, order, page: 1 };
}

function applyFilter(
  state: ListQuery,
  key: string,
  value: FilterPrimitive | undefined,
): ListQuery {
  const next: ListQuery = { ...state, page: 1 };
  if (value === undefined || value === "" || value === null) delete next[key];
  else next[key] = value;
  return next;
}

function applySearch(state: ListQuery, search: string): ListQuery {
  const next: ListQuery = { ...state, page: 1 };
  if (search) next.search = search;
  else delete next.search;
  return next;
}

function applyDateRange(
  state: ListQuery,
  from: string | undefined,
  to: string | undefined,
): ListQuery {
  const next: ListQuery = { ...state, page: 1 };
  if (from) next.dateFrom = from; else delete next.dateFrom;
  if (to) next.dateTo = to; else delete next.dateTo;
  return next;
}

function clearFiltersOnly(state: ListQuery): ListQuery {
  const cleared: ListQuery = { page: 1, limit: state.limit };
  if (state.sortBy) cleared.sortBy = state.sortBy;
  if (state.order) cleared.order = state.order;
  return cleared;
}

function reduce(state: ListQuery, action: Action): ListQuery {
  switch (action.type) {
    case "page": return { ...state, page: Math.max(1, action.page) };
    case "limit": return { ...state, limit: Math.max(1, action.limit), page: 1 };
    case "sort": return applySort(state, action.sortBy);
    case "search": return applySearch(state, action.search);
    case "dateRange": return applyDateRange(state, action.from, action.to);
    case "filter": return applyFilter(state, action.key, action.value);
    case "clearFilters": return clearFiltersOnly(state);
    default: return state;
  }
}

function buildInitial(opts: UseDataTableOptions): ListQuery {
  const initial: ListQuery = { page: 1, limit: opts.pageSize ?? 20 };
  if (opts.defaultSort) {
    initial.sortBy = opts.defaultSort.sortBy;
    initial.order = opts.defaultSort.order;
  }
  return initial;
}

function useSearchDebounce(
  pending: string,
  delay: number,
  dispatch: Dispatch<Action>,
): void {
  useEffect(() => {
    const t = setTimeout(() => dispatch({ type: "search", search: pending }), delay);
    return (): void => clearTimeout(t);
  }, [pending, delay, dispatch]);
}

function toggleSet(prev: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const n = new Set(prev);
  if (n.has(id)) n.delete(id); else n.add(id);
  return n;
}

function toggleAllSet(
  prev: ReadonlySet<string>,
  ids: readonly string[],
): ReadonlySet<string> {
  const allIn = ids.length > 0 && ids.every((i) => prev.has(i));
  const next = new Set(prev);
  if (allIn) ids.forEach((i) => next.delete(i));
  else ids.forEach((i) => next.add(i));
  return next;
}

function useSelection(): DataTableSelection {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  return useMemo<DataTableSelection>(() => ({
    selected,
    toggle: (id) => setSelected((p) => toggleSet(p, id)),
    toggleAll: (ids) => setSelected((p) => toggleAllSet(p, ids)),
    clear: () => setSelected(new Set()),
    isAllSelected: (ids) => ids.length > 0 && ids.every((i) => selected.has(i)),
    isSomeSelected: (ids) =>
      ids.some((i) => selected.has(i)) && !ids.every((i) => selected.has(i)),
  }), [selected]);
}

function buildState(
  query: ListQuery,
  pendingSearch: string,
  dispatch: Dispatch<Action>,
  setPendingSearch: Dispatch<React.SetStateAction<string>>,
  selection: DataTableSelection,
): DataTableState {
  return {
    query,
    pendingSearch,
    selection,
    setPage: (page) => dispatch({ type: "page", page }),
    setLimit: (limit) => dispatch({ type: "limit", limit }),
    setSort: (sortBy) => dispatch({ type: "sort", sortBy }),
    setSearch: (value) => setPendingSearch(value),
    setDateRange: (from, to) => dispatch({ type: "dateRange", from, to }),
    setFilter: (key, value): void => {
      if (RESERVED_KEYS.has(key)) return;
      dispatch({ type: "filter", key, value });
    },
    clearFilters: () => dispatch({ type: "clearFilters" }),
  };
}

export function useDataTable(options: UseDataTableOptions = {}): DataTableState {
  const [query, dispatch] = useReducer(reduce, options, buildInitial);
  const [pendingSearch, setPendingSearch] = useState<string>("");
  useSearchDebounce(
    pendingSearch,
    options.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS,
    dispatch,
  );
  const selection = useSelection();
  return useMemo<DataTableState>(
    (): DataTableState =>
      buildState(query, pendingSearch, dispatch, setPendingSearch, selection),
    [query, pendingSearch, selection],
  );
}
